import type { FormField } from './uniform-form-schema';

export type FormValue = string | string[];
export type FormValues = Record<string, FormValue>;
export type FormErrors = Record<string, string>;

export type FormValidationMessages = {
	required: string;
	invalidEmail: string;
	businessEmail: string;
	firstNameRequired: string;
	lastNameRequired: string;
	companyRequired: string;
	phoneRequired: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PERSONAL_EMAIL_DOMAINS = new Set([
	'aol.com',
	'gmail.com',
	'hotmail.com',
	'icloud.com',
	'live.com',
	'outlook.com',
	'yahoo.com',
]);
const ALLOWED_INTERCOM_APP_IDS = new Set(['p0ww63i2']);

const isEmpty = (value: FormValue | undefined): boolean =>
	Array.isArray(value) ? value.length === 0 : !value?.trim();

export const getVisibleFormFields = (fields: FormField[], values: FormValues): FormField[] => {
	const visibleFields: FormField[] = [];
	for (const field of fields) {
		visibleFields.push(field);
		if (field.type !== 'formCustomChoiceField' && field.type !== 'formCountryOrStateField') {
			continue;
		}
		const selected = values[field.payloadKey];
		const selectedValues = Array.isArray(selected) ? selected : selected ? [selected] : [];
		for (const option of field.options) {
			if (selectedValues.includes(option.value)) {
				visibleFields.push(...getVisibleFormFields(option.conditionalFields, values));
			}
		}
	}
	return visibleFields;
};

export const validateFormValues = (
	fields: FormField[],
	values: FormValues,
	messages: FormValidationMessages,
	// eslint-disable-next-line complexity -- each supported schema field has distinct validation rules.
): FormErrors => {
	const errors: FormErrors = {};
	for (const field of getVisibleFormFields(fields, values)) {
		switch (field.type) {
			case 'formNamesFields':
				if (field.required && isEmpty(values['firstName']))
					errors['firstName'] = messages.firstNameRequired;
				if (field.required && isEmpty(values['lastName']))
					errors['lastName'] = messages.lastNameRequired;
				break;
			case 'formEmailField': {
				const email = String(values['email'] ?? '')
					.trim()
					.toLowerCase();
				if (!email) errors['email'] = field.requiredMessage ?? messages.required;
				else if (!EMAIL_PATTERN.test(email)) errors['email'] = messages.invalidEmail;
				else if (field.businessEmailOnly && PERSONAL_EMAIL_DOMAINS.has(email.split('@')[1] ?? '')) {
					errors['email'] = messages.businessEmail;
				}
				break;
			}
			case 'formCompanyField':
				if (field.required && isEmpty(values['company'])) {
					errors['company'] = field.requiredMessage ?? messages.companyRequired;
				}
				break;
			case 'formPhoneNumberField':
				if (field.required && isEmpty(values['number'])) errors['number'] = messages.phoneRequired;
				break;
			case 'formCustomTextField':
			case 'formDatepickerField':
			case 'formCustomChoiceField':
			case 'formCountryOrStateField':
				if (field.required && isEmpty(values[field.payloadKey])) {
					errors[field.payloadKey] = field.requiredMessage ?? messages.required;
				}
				break;
		}
	}
	return errors;
};

export const validateFormConfiguration = ({
	fields,
	formId,
	formType,
	intercomAppId,
	intercomTracking,
}: {
	fields: FormField[];
	formId?: string;
	formType?: string;
	intercomAppId?: string;
	intercomTracking?: boolean;
}): string[] => {
	const issues: string[] = [];
	const payloadKeys = new Set<string>();
	let emailFieldCount = 0;

	if (!formId?.trim()) issues.push('formId is required');
	if (!formType?.trim()) issues.push('formType is required');
	if (intercomTracking && (!intercomAppId || !ALLOWED_INTERCOM_APP_IDS.has(intercomAppId))) {
		issues.push('intercomAppId is missing or is not allowlisted');
	}

	// eslint-disable-next-line complexity -- validates the complete recursive authoring contract.
	const visit = (currentFields: FormField[]) => {
		for (const field of currentFields) {
			if (field.type === 'formEmailField') emailFieldCount += 1;
			const keys =
				field.type === 'formNamesFields'
					? ['firstName', 'lastName']
					: field.type === 'formEmailField'
						? ['email']
						: field.type === 'formCompanyField'
							? ['company']
							: field.type === 'formPhoneNumberField'
								? ['number']
								: [field.payloadKey];
			for (const key of keys) {
				if (!key) {
					issues.push('Custom fields require a payloadKey');
					continue;
				}
				if (payloadKeys.has(key)) issues.push(`Duplicate payload key: ${key}`);
				payloadKeys.add(key);
			}
			if (field.type === 'formCustomChoiceField' || field.type === 'formCountryOrStateField') {
				if (!field.options.length)
					issues.push(`Choice field ${field.payloadKey} requires an option`);
				const optionValues = new Set<string>();
				for (const option of field.options) {
					if (!option.value)
						issues.push(`Choice field ${field.payloadKey} has an empty option value`);
					if (optionValues.has(option.value)) {
						issues.push(
							`Choice field ${field.payloadKey} has duplicate option value: ${option.value}`,
						);
					}
					optionValues.add(option.value);
					visit(option.conditionalFields);
				}
			}
		}
	};

	visit(fields);
	if (emailFieldCount !== 1) issues.push('The form requires exactly one email field');
	return issues;
};

const sanitizeString = (value: string): string =>
	value.trim().replace(/</g, '＜').replace(/>/g, '＞');

export const buildVisibleFormPayload = (fields: FormField[], values: FormValues): FormValues => {
	const payload: FormValues = {};
	for (const field of getVisibleFormFields(fields, values)) {
		const keys =
			field.type === 'formNamesFields'
				? ['firstName', 'lastName']
				: field.type === 'formEmailField'
					? ['email']
					: field.type === 'formCompanyField'
						? ['company']
						: field.type === 'formPhoneNumberField'
							? ['number']
							: [field.payloadKey];
		for (const key of keys) {
			const value = values[key];
			if (Array.isArray(value)) payload[key] = value.map(sanitizeString).join(',');
			else if (typeof value === 'string') {
				payload[key] =
					key === 'email' ? sanitizeString(value).toLowerCase() : sanitizeString(value);
			}
		}
	}
	return payload;
};
